import React, { useState } from 'react';
import PlatformShell from '../../components/PlatformShell';
import { DataTable, Badge } from '../../components/Ui';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { randomPassword } from '../../utils/staffLogin';

export default function PlatformAdmins() {
  const { data, loading, error, reload } = useLiveData(() => ipApi.platformAdmins(), []);
  const admins = data?.admins || [];
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [created, setCreated] = useState(null);

  const createAdmin = async (e) => {
    e.preventDefault();
    setBusy('create');
    setMsg('');
    setCreated(null);
    try {
      const password = form.password || randomPassword();
      const res = await ipApi.platformCreateAdmin({
        name: form.name.trim(),
        email: form.email.toLowerCase().trim(),
        password,
      });
      setCreated({ ...res.admin, password: res.temporaryPassword || password });
      setForm({ name: '', email: '', password: '' });
      await reload();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const toggleActive = async (row) => {
    setBusy(row.adminId);
    setMsg('');
    try {
      await ipApi.platformPatchAdmin(row.adminId, { active: row.active === false });
      await reload();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const resetPassword = async (row) => {
    if (!window.confirm(`Reset password for ${row.email}?`)) return;
    setBusy(row.adminId);
    setMsg('');
    try {
      const pwd = randomPassword();
      const res = await ipApi.platformPatchAdmin(row.adminId, { password: pwd });
      setCreated({ email: row.email, name: row.name, password: res.temporaryPassword || pwd });
      await reload();
    } catch (err) {
      setMsg(err.message);
    } finally {
      setBusy('');
    }
  };

  const columns = [
    { key: 'name', label: 'Name', render: (r) => <strong>{r.name}</strong> },
    { key: 'email', label: 'Email', render: (r) => <code>{r.email}</code> },
    {
      key: 'active',
      label: 'Status',
      render: (r) => <Badge tone={r.active !== false ? 'success' : 'warning'}>{r.active !== false ? 'Active' : 'Disabled'}</Badge>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <div className="row-actions">
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy === r.adminId} onClick={() => toggleActive(r)}>
            {r.active !== false ? 'Disable' : 'Enable'}
          </button>
          <button type="button" className="btn btn-sm btn-secondary" disabled={busy === r.adminId} onClick={() => resetPassword(r)}>
            Reset password
          </button>
        </div>
      ),
    },
  ];

  return (
    <PlatformShell title="HCEN Platform Admins" subtitle="Owner accounts for networks.hcen.in/platform">
      {error && <div className="error mb">{error}</div>}
      {msg && <div className="error mb">{msg}</div>}

      {created && (
        <div className="credential-card card mb">
          <h3>Credentials — share securely</h3>
          <dl className="credential-dl">
            <dt>URL</dt><dd><code>https://networks.hcen.in/platform/login</code></dd>
            <dt>Email</dt><dd><code>{created.email}</code></dd>
            <dt>Password</dt><dd><code>{created.password}</code></dd>
          </dl>
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCreated(null)}>Dismiss</button>
        </div>
      )}

      <div className="form-grid mb">
        <div className="card">
          <h3>Add platform admin</h3>
          <form onSubmit={createAdmin}>
            <div className="field"><label>Name</label><input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="field"><label>Email</label><input className="input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="owner@hcen.in" /></div>
            <div className="field"><label>Password (optional)</label><input className="input" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Auto-generated if blank" /></div>
            <button type="submit" className="btn btn-primary" disabled={busy === 'create'}>{busy === 'create' ? 'Creating…' : 'Create admin'}</button>
          </form>
        </div>
        <div className="card">
          <h3>Security checklist</h3>
          <ul className="hint" style={{ lineHeight: 1.8, margin: 0, paddingLeft: 18 }}>
            <li>Set <code>IP_AUTH_SECRET</code> and <code>IP_AUTH_SALT</code> in Lambda (not defaults)</li>
            <li>Set <code>HCEN_CROSS_LINK_SECRET</code> on IP + OP Lambdas for hospital linking</li>
            <li>Disable unused admin accounts</li>
            <li>Force password change after sharing temp passwords</li>
          </ul>
        </div>
      </div>

      <div className="card">
        <h3>Platform admins ({loading ? '…' : admins.length})</h3>
        <DataTable columns={columns} rows={admins} keyField="adminId" emptyMessage="No platform admins found. Run seed or create one above." />
      </div>
    </PlatformShell>
  );
}
